import * as THREE from 'three';

import { isDefined } from '../variables';

/**
 * Rounds a number to the desired number of decimals
 */
export function roundToDecimals(value: number, decimals: number) {
  return Number(value.toFixed(decimals));
}

/**
 * Returns a value indicating whether the specified value is a valid number and that it's within a
 * safe value range.
 */
export const isValidNumber = (n: number | string | null | undefined) => {
  const isValid = (val: number) =>
    !Number.isNaN(val) && val >= Number.MIN_SAFE_INTEGER && val <= Number.MAX_SAFE_INTEGER;

  if (typeof n === 'string') {
    return /^-?[0-9]+(\.[0-9]+)?$/.test(n) ? isValid(Number(n)) : false;
  }

  if (!isDefined(n)) {
    return false;
  }

  return isValid(n);
};

/**
 * Returns a value indicating whether the specified value is a valid double precision floating
 * point number.
 */
export const isValidDecimal = (value: string) =>
  isValidNumber(value) && !Number.isNaN(parseFloat(value));

/**
 * Generates an array of all powers of two up to a given value, assuming the value is a power of two.
 * @param value The upper limit value, which should be a power of two.
 * @returns An array of numbers, each a power of two, starting from 1 up to the given value.
 * @throws Error if the provided value is not a power of two.
 *
 * This function first checks if the given value is a power of two, and once confirmed that the value
 * is a power of two, the function creates an array, starting from 1, and keeps doubling the number
 * and adding it to the array until it reaches or exceeds the provided value.
 * This generates a sequence of all powers of two from 1 to the input value.
 */
export const getPowersOfTwo = (value: number): number[] => {
  if (!THREE.MathUtils.isPowerOfTwo(value)) {
    throw new Error('The provided value is not a power of two.');
  }

  const powers = [];
  for (let i = 1; i <= value; i *= 2) {
    powers.push(i);
  }

  return powers;
};
